import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import httpRequest from '../utils/request';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useModalEscape } from '../utils/useModalEscape';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaUser,
  FaClock,
  FaCheck,
  FaTimes,
  FaArrowLeft,
  FaUtensils,
  FaUsers,
  FaChair
} from 'react-icons/fa';

export default function RestaurantSalon() {
  const { user } = useSelector((s) => s.auth);
  const navigate = useNavigate();

  // State
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [activeSector, setActiveSector] = useState('Todos');
  const [configMode, setConfigMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [waiterId, setWaiterId] = useState('');
  const [dinersCount, setDinersCount] = useState(1);

  // Table Configuration Modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configTableData, setConfigTableData] = useState({
    _id: '',
    number: '',
    name: '',
    capacity: 2,
    sector: 'Salón Principal'
  });
  const [isEditConfig, setIsEditConfig] = useState(false);

  // Timer state for real-time elapsed time updates
  const [, setTick] = useState(0);

  // Autofocus refs
  const numberRef = useRef(null);
  const openDinersRef = useRef(null);

  // ESC key handlers for modals
  useModalEscape(showOpenModal, () => {
    setShowOpenModal(false);
    setSelectedTable(null);
  });

  useModalEscape(showConfigModal, () => {
    setShowConfigModal(false);
  });

  // Fetch Tables
  const fetchTables = async () => {
    try {
      setLoading(true);
      const { data } = await httpRequest.get('/restaurant/tables');
      setTables(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('❌ Error al cargar las mesas');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Waiters (active users)
  const fetchWaiters = async () => {
    try {
      const { data } = await httpRequest.get('/users');
      // filter to active and useful roles or just show all active users
      if (Array.isArray(data)) {
        setWaiters(data.filter(u => u.isActive));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTables();
    fetchWaiters();
  }, []);

  // Polling for table updates every 10 seconds and notify when a table is ready
  const prevTablesRef = useRef([]);
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await httpRequest.get('/restaurant/tables');
        const newTables = Array.isArray(data) ? data : [];
        // Compare with previous tables to detect newly ready tables
        const prev = prevTablesRef.current;
        newTables.forEach(t => {
          const wasReady = prev.find(p => p._id === t._id && p.readyToServe);
          if (t.readyToServe && (!wasReady || !wasReady.readyToServe)) {
            toast.info(`✅ Mesa ${t.name || t.number} está lista para servir`);
          }
        });
        prevTablesRef.current = newTables;
        setTables(newTables);
      } catch (err) {
        console.error(err);
        toast.error('❌ Error al actualizar mesas');
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Keep previous tables updated after manual fetches (e.g., after open/close actions)
  useEffect(() => {
    prevTablesRef.current = tables;
  }, [tables]);

  // Update timer every 30 seconds to recalculate elapsed time dynamically
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(prev => prev + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Autofocus on number input when config modal is shown
  useEffect(() => {
    if (showConfigModal && numberRef.current) {
      setTimeout(() => numberRef.current.focus(), 100);
    }
  }, [showConfigModal]);

  // Autofocus on diners input when open modal is shown
  useEffect(() => {
    if (showOpenModal && openDinersRef.current) {
      setTimeout(() => openDinersRef.current.focus(), 100);
    }
  }, [showOpenModal]);

  // Sectors derived from database
  const sectors = ['Todos', ...new Set(tables.map(t => t.sector || 'Salón Principal'))];

  // Calculate elapsed time in minutes
  const getElapsedTime = (createdAtString) => {
    if (!createdAtString) return '';
    const diffMs = Date.now() - new Date(createdAtString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 0) return '0 min';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Table actions
  const handleTableClick = (table) => {
    if (configMode) {
      // In config mode, click triggers edit modal
      openEditConfigModal(table);
      return;
    }

    if (table.status === 'free') {
      setSelectedTable(table);
      // Pre-select current logged-in user if available in waiters
      const isUserWaiter = waiters.some(w => w._id === user._id);
      setWaiterId(isUserWaiter ? user._id : (waiters[0]?._id || ''));
      setDinersCount(2);
      setShowOpenModal(true);
    } else {
      // Redirect to POS screen for active order
      navigate(`/restaurant-pos/${table._id}`);
    }
  };

  const handleMarkServed = async (tableId, e) => {
    e.stopPropagation();
    try {
      await httpRequest.post(`/restaurant/tables/${tableId}/served`);
      toast.success('✅ Mesa marcada como servida');
      // Refresh tables to update UI
      fetchTables();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al actualizar estado de la mesa';
      toast.error(`❌ ${msg}`);
    }
  };

  // Submit table opening
  const handleOpenTable = async (e) => {
    e.preventDefault();
    if (!selectedTable) return;
    if (!waiterId) {
      toast.warn('Por favor selecciona un mozo');
      return;
    }

    try {
      await httpRequest.post('/restaurant/orders/open', {
        tableId: selectedTable._id,
        waiterId,
        dinersCount: Number(dinersCount)
      });
      toast.success(`🎉 Mesa ${selectedTable.name || selectedTable.number} abierta con éxito`);
      setShowOpenModal(false);
      // Go directly to POS screen to load products
      navigate(`/restaurant-pos/${selectedTable._id}`);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al abrir la mesa';
      toast.error(`❌ ${msg}`);
    }
  };

  // Add/Edit Table API call
  const handleSaveTableConfig = async (e) => {
    e.preventDefault();
    const { _id, number, name, capacity, sector } = configTableData;
    if (!number) {
      toast.warn('El número de mesa es requerido');
      return;
    }

    try {
      const payload = {
        number: Number(number),
        name: name || `Mesa ${number}`,
        capacity: Number(capacity || 2),
        sector: sector || 'Salón Principal'
      };

      if (isEditConfig) {
        await httpRequest.put(`/restaurant/tables/${_id}`, payload);
        toast.success('✅ Mesa modificada exitosamente');
      } else {
        await httpRequest.post('/restaurant/tables', payload);
        toast.success('✅ Mesa creada exitosamente');
      }

      setShowConfigModal(false);
      fetchTables();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al guardar la mesa';
      toast.error(`❌ ${msg}`);
    }
  };

  // Delete Table
  const handleDeleteTable = async (tableId, event) => {
    event.stopPropagation(); // Avoid triggering open modal/edit modal
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta mesa?')) return;

    try {
      await httpRequest.delete(`/restaurant/tables/${tableId}`);
      toast.success('🗑️ Mesa eliminada correctamente');
      fetchTables();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al eliminar la mesa';
      toast.error(`❌ ${msg}`);
    }
  };

  // Modal helper triggers
  const openNewConfigModal = () => {
    setIsEditConfig(false);
    setConfigTableData({
      _id: '',
      number: tables.length > 0 ? Math.max(...tables.map(t => t.number)) + 1 : 1,
      name: '',
      capacity: 2,
      sector: 'Salón Principal'
    });
    setShowConfigModal(true);
  };

  const openEditConfigModal = (table) => {
    setIsEditConfig(true);
    setConfigTableData({
      _id: table._id,
      number: table.number,
      name: table.name || '',
      capacity: table.capacity || 2,
      sector: table.sector || 'Salón Principal'
    });
    setShowConfigModal(true);
  };

  // Filtering
  const filteredTables = tables.filter(t => {
    if (activeSector === 'Todos') return true;
    return (t.sector || 'Salón Principal') === activeSector;
  });

  return (
    <div className="salon-container">
      {/* Premium styling injected directly to preserve integrity without complex configs */}
      <style>{`
        .salon-container {
          padding: 24px;
          max-width: 1300px;
          margin: 0 auto;
          font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1e293b;
          min-height: 100vh;
        }

        .salon-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .salon-title-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .salon-title {
          font-size: 28px;
          font-weight: 800;
          margin: 0;
          background: linear-gradient(135deg, #0f172a 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .salon-header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn-action {
          padding: 10px 18px;
          border-radius: 12px;
          border: none;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .btn-nav {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(8px);
          color: #334155;
          border: 1px solid #e2e8f0;
        }

        .btn-nav:hover {
          background: #f1f5f9;
          transform: translateY(-2px);
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
        }

        .btn-primary:hover {
          box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.4);
          transform: translateY(-2px);
        }

        .config-mode-switch-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.8);
          padding: 8px 16px;
          border-radius: 9999px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          user-select: none;
        }

        .ready-timer {
          font-weight: 600;
          color: #10b981;
        }

        .switch-label {
          font-size: 13px;
          font-weight: 700;
          color: #475569;
        }

        .switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: .3s;
          border-radius: 34px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .3s;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        input:checked + .slider {
          background-color: #10b981;
        }

        input:checked + .slider:before {
          transform: translateX(20px);
        }

        /* Sectors Navigation */
        .sectors-nav {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          overflow-x: auto;
          padding-bottom: 8px;
          scrollbar-width: thin;
        }

        .sector-tab {
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid #e2e8f0;
          border-radius: 9999px;
          cursor: pointer;
          font-weight: 700;
          font-size: 14px;
          color: #64748b;
          white-space: nowrap;
          transition: all 0.25s ease;
        }

        .sector-tab:hover {
          color: #3b82f6;
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .sector-tab.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
          box-shadow: 0 4px 10px rgba(59, 130, 246, 0.25);
        }

        /* Grid of Tables */
        .tables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 20px;
        }

        .table-card {
          border-radius: 20px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
          cursor: pointer;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          height: 170px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .table-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.12);
        }

        /* Card Status Color Variations */
        .table-card.free {
          border-left: 6px solid #94a3b8;
          background: linear-gradient(to bottom right, #ffffff, #f8fafc);
        }
        .table-card.free:hover {
          border-color: #64748b;
        }
        .table-card.ready {
          border-left: 6px solid #10b981;
          background: linear-gradient(to bottom right, #f0fdf4, #e6fffa);
          animation: gentle-pulse-ready 2s infinite ease-in-out;
        }
        .table-card.ready:hover {
          border-color: #059669;
        }
        @keyframes gentle-pulse-ready {
          0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.3); }
          70% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .table-card.occupied {
          border-left: 6px solid #ef4444;
          background: linear-gradient(to bottom right, #ffffff, #fef2f2);
        }
        .table-card.occupied:hover {
          border-color: #dc2626;
        }

        .table-card.waiting_payment {
          border-left: 6px solid #f59e0b;
          background: linear-gradient(to bottom right, #ffffff, #fffbeb);
          animation: gentle-pulse 2s infinite ease-in-out;
        }
        .table-card.waiting_payment:hover {
          border-color: #d97706;
        }

        @keyframes gentle-pulse {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3); }
          70% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: center;
        }

        .table-number {
          font-size: 24px;
          font-weight: 900;
          color: #1e293b;
        }

        .table-capacity {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .table-middle {
          margin: 12px 0;
          text-align: center;
        }

        .table-name {
          font-size: 15px;
          font-weight: 700;
          color: #334155;
          margin-bottom: 4px;
        }

        .table-waiter {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .table-footer {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px dashed #e2e8f0;
          padding-top: 8px;
          font-size: 13px;
        }

        .table-total {
          font-weight: 800;
          color: #0f172a;
          font-size: 15px;
        }

        .table-time {
          color: #64748b;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        /* Config overlay actions on card */
        .card-config-actions {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          opacity: 0;
          transition: opacity 0.25s ease;
          border-radius: 20px;
          z-index: 10;
        }

        .table-card:hover .card-config-actions {
          opacity: 1;
        }

        .btn-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          cursor: pointer;
          color: white;
          transition: transform 0.2s;
        }

        .btn-circle:hover {
          transform: scale(1.1);
        }

        .btn-edit { background: #3b82f6; }
        .btn-delete { background: #ef4444; }
        .btn-served { background: #10b981; }

        /* General Modal Styling */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fade-in 0.2s ease-out;
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          padding: 28px;
          width: 95%;
          max-width: 460px;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
          animation: slide-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .btn-close {
          background: none;
          border: none;
          color: #64748b;
          font-size: 20px;
          cursor: pointer;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 700;
          color: #475569;
          margin-bottom: 6px;
        }

        .form-input {
          width: 100%;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid #cbd5e1;
          font-size: 14px;
          box-sizing: border-box;
          outline: none;
          transition: border-color 0.2s;
        }

        .form-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .diner-counter-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn-counter {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          color: #334155;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-counter:hover {
          background: #e2e8f0;
        }

        .counter-value {
          font-size: 16px;
          font-weight: 800;
          width: 30px;
          text-align: center;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }

        .btn-muted {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .btn-muted:hover {
          background: #e2e8f0;
        }

        .no-tables {
          background: white;
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          border: 1px dashed #cbd5e1;
          color: #64748b;
        }
      `}</style>

      {/* Title & Toolbar Header */}
      <header className="salon-header">
        <div className="salon-title-wrapper">
          <FaUtensils style={{ fontSize: 28, color: '#3b82f6' }} />
          <h1 className="salon-title">Salón de Mesas</h1>
        </div>
        <div className="salon-header-actions">
          <button onClick={() => navigate('/dashboard')} className="btn-action btn-nav">
            <FaArrowLeft /> Volver
          </button>
          
          <div className="config-mode-switch-wrapper">
            <span className="switch-label">Modo Configuración</span>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={configMode} 
                onChange={(e) => setConfigMode(e.target.checked)} 
              />
              <span className="slider"></span>
            </label>
          </div>

          {configMode && (
            <button onClick={openNewConfigModal} className="btn-action btn-primary">
              <FaPlus /> Nueva Mesa
            </button>
          )}
        </div>
      </header>

      {/* Sectors horizontal selector */}
      <nav className="sectors-nav">
        {sectors.map((sec) => (
          <button
            key={sec}
            onClick={() => setActiveSector(sec)}
            className={`sector-tab ${activeSector === sec ? 'active' : ''}`}
          >
            {sec}
          </button>
        ))}
      </nav>

      {/* Main Grid / Tables list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', fontSize: '16px', color: '#64748b', fontWeight: 600 }}>
          🔄 Cargando mapa de salón...
        </div>
      ) : filteredTables.length === 0 ? (
        <div className="no-tables">
          <h3>No se encontraron mesas cargadas</h3>
          {configMode ? (
            <p>Comenzá agregando mesas con el botón "Nueva Mesa" de arriba.</p>
          ) : (
            <p>Activá el **Modo Configuración** arriba para crear tus primeras mesas y sectores.</p>
          )}
        </div>
      ) : (
        <div className="tables-grid">
          {filteredTables.map((table) => {
            const hasOrder = table.activeOrderId != null;
            const orderTotal = hasOrder ? (table.activeOrderId.total || 0) : 0;
            const elapsedTime = hasOrder ? getElapsedTime(table.activeOrderId.createdAt) : '';
            const waiterName = hasOrder ? (table.activeOrderId.waiterId?.name || 'Mozo') : '';
            const _diners = hasOrder ? (table.activeOrderId.dinersCount || 1) : '';

            return (
              <div
                key={table._id}
                onClick={() => handleTableClick(table)}
                className={`table-card ${table.status} ${table.readyToServe ? 'ready' : ''}`}
              >
                {/* Configuration Actions Overlay */}
                {configMode ? (
                  <div className="card-config-actions" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEditConfigModal(table)} className="btn-circle btn-edit" title="Editar Mesa">
                      <FaEdit />
                    </button>
                    <button onClick={(e) => handleDeleteTable(table._id, e)} className="btn-circle btn-delete" title="Eliminar Mesa">
                      <FaTrash />
                    </button>
                  </div>
                ) : null}
                {/* Waiter button to mark served when table is ready */}
                {!configMode && table.readyToServe && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '8px' }}>
                    <button
                      onClick={(e) => handleMarkServed(table._id, e)}
                      className="btn-action btn-served"
                    >
                      <FaCheck /> Servir
                    </button>
                    <span className="ready-timer" style={{ marginTop: '4px', fontSize: '12px', color: '#10b981' }}>
                       {getElapsedTime(table.readyAt || table.updatedAt)}
                    </span>
                  </div>
                )}

                {/* Table Header Details */}
                <div className="table-header">
                  <span className="table-number">#{table.number}</span>
                  <span className="table-capacity">
                    <FaChair style={{ fontSize: 11 }} /> {table.capacity}
                  </span>
                </div>

                {/* Table Center Text */}
                <div className="table-middle">
                  <div className="table-name">{table.name || `Mesa ${table.number}`}</div>
                  {table.status !== 'free' && (
                    <div className="table-waiter">
                      <FaUser style={{ fontSize: 10 }} /> {waiterName}
                    </div>
                  )}
                  {table.status === 'free' && (
                    <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Disponible</div>
                  )}
                </div>

                {/* Table Footer Details */}
                <div className="table-footer">
                  {table.status === 'free' ? (
                    <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700 }}>
                      Sector: {table.sector || 'Salón'}
                    </div>
                  ) : (
                    <>
                      <div className="table-total">${Number(orderTotal).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
                      <div className="table-time">
                        <FaClock style={{ fontSize: 11 }} /> {elapsedTime}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL: Apertura de Mesa (Free Table click) */}
      {showOpenModal && (
        <div className="modal-overlay" onClick={() => setShowOpenModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🔓 Abrir Mesa {selectedTable?.name || selectedTable?.number}</h2>
              <button onClick={() => setShowOpenModal(false)} className="btn-close"><FaTimes /></button>
            </div>
            <form onSubmit={handleOpenTable}>
              <div className="form-group">
                <label className="form-label">Mozo Asignado</label>
                <select
                  value={waiterId}
                  onChange={(e) => setWaiterId(e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="" disabled>Seleccionar mozo...</option>
                  {waiters.map(w => (
                    <option key={w._id} value={w._id}>{w.name} ({w.role === 'owner' ? 'Dueño' : w.role})</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Cantidad de Comensales</label>
                <div className="diner-counter-wrapper">
                  <button
                    type="button"
                    onClick={() => setDinersCount(prev => Math.max(1, prev - 1))}
                    className="btn-counter"
                  >
                    -
                  </button>
                  <span className="counter-value">{dinersCount}</span>
                  <button
                    type="button"
                    onClick={() => setDinersCount(prev => prev + 1)}
                    className="btn-counter"
                  >
                    +
                  </button>
                  <input
                    ref={openDinersRef}
                    type="number"
                    value={dinersCount}
                    onChange={(e) => setDinersCount(Math.max(1, Number(e.target.value)))}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginLeft: 8 }}>
                    <FaUsers style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} /> personas
                  </span>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowOpenModal(false)} className="btn-action btn-muted">
                  Cancelar
                </button>
                <button type="submit" className="btn-action btn-primary">
                  Abrir Salón
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Configuración de Mesa (Crear/Editar) */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {isEditConfig ? '✏️ Editar Mesa' : '➕ Agregar Nueva Mesa'}
              </h2>
              <button onClick={() => setShowConfigModal(false)} className="btn-close"><FaTimes /></button>
            </div>
            <form onSubmit={handleSaveTableConfig}>
              <div className="form-group">
                <label className="form-label">Número de Mesa</label>
                <input
                  ref={numberRef}
                  type="number"
                  required
                  value={configTableData.number}
                  onChange={(e) => setConfigTableData({ ...configTableData, number: e.target.value })}
                  className="form-input"
                  placeholder="Ej: 5"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Nombre / Etiqueta (Opcional)</label>
                <input
                  type="text"
                  value={configTableData.name}
                  onChange={(e) => setConfigTableData({ ...configTableData, name: e.target.value })}
                  className="form-input"
                  placeholder="Ej: Mesa VIP"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Capacidad (Sillas)</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={configTableData.capacity}
                  onChange={(e) => setConfigTableData({ ...configTableData, capacity: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sector / Área</label>
                <select
                  value={configTableData.sector}
                  onChange={(e) => setConfigTableData({ ...configTableData, sector: e.target.value })}
                  className="form-input"
                >
                  <option value="Salón Principal">Salón Principal</option>
                  <option value="Terraza">Terraza</option>
                  <option value="Vereda">Vereda</option>
                  <option value="VIP">VIP</option>
                  <option value="Barra">Barra</option>
                </select>
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    placeholder="O escribí un sector nuevo..."
                    onChange={(e) => e.target.value.trim() && setConfigTableData({ ...configTableData, sector: e.target.value.trim() })}
                    className="form-input"
                    style={{ fontSize: 13, padding: '8px 10px' }}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowConfigModal(false)} className="btn-action btn-muted">
                  Cancelar
                </button>
                <button type="submit" className="btn-action btn-primary">
                  {isEditConfig ? 'Guardar Cambios' : 'Crear Mesa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
